import { contactAnnotationsPayload } from './contactAnnotations';

export async function getMetrics(trialID, trialName) {
    const token = localStorage.getItem("token");
    let url = window.SERVER_URL + `/a/get-dict-metrics`;
    if (trialID) {
      url += `?trial_id=${encodeURIComponent(trialID)}`;
      if (trialName) {
        url += `&trial_name=${encodeURIComponent(trialName)}`;
      }
    }
    return fetch(url, {
      headers: {
        Authorization: 'Bearer ' + token
      }
    })
      .then(res => res.json())
      .catch(err => console.log(err));
}

/** Backend may emit non-standard JSON tokens (NaN/Infinity); coerce before parse. */
export function sanitizeNonStandardJson(text) {
  return String(text)
    .replace(/-Infinity/g, 'null')
    .replace(/\bInfinity\b/g, 'null')
    .replace(/\bNaN\b/g, 'null');
}

export function parseJsonAllowingNonStandardTokens(text) {
  return JSON.parse(sanitizeNonStandardJson(text));
}

function handleResponse(response) {
    if (response.ok) {
      return response.text().then((text) => parseJsonAllowingNonStandardTokens(text));
    } else {
      return Promise.reject(response);
    }
  }
  
export async function getValuesMetrics(trialID, type, name, trialName) {
  if (type && name) {
    const token = localStorage.getItem("token");
    let url = window.SERVER_URL + `/a/get-values-metrics?trial_id=${trialID}&type=${type}&name=${encodeURIComponent(name)}`;
    if (trialName) {
      url += `&trial_name=${encodeURIComponent(trialName)}`;
    }
    return fetch(url, {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(handleResponse)
      .catch((err) => {
        console.log(err);
      });
  }
}

export async function getSensorOffset(trialID) {
  const token = localStorage.getItem("token");
  const url = window.SERVER_URL + `/a/get-sensor-offset?trial_id=${encodeURIComponent(trialID)}`;
  return fetch(url, { headers: { Authorization: 'Bearer ' + token } })
    .then((res) => (res.ok ? res.json() : null))
    .catch((err) => { console.log(err); return null; });
}

/**
 * Fetch floor-plane metadata for a trial (plane, fps, detrend slopes).
 * GET /a/get-floor-detect?trial_id=…
 * Returns the JSON body, or null on network / non-OK response.
 * Failure body `{ status: "failure", reason: "no floor_detect for this trial" }`
 * is returned as-is when the HTTP response is OK (caller checks status / floor_detect).
 * The browser does not read S3 floor artifacts; the API returns trials.floor_detect.
 */
export async function getFloorDetect(trialID) {
  if (trialID == null || trialID === '') return null;
  const token = localStorage.getItem("token");
  const url =
    window.SERVER_URL +
    `/a/get-floor-detect?trial_id=${encodeURIComponent(trialID)}`;
  return fetch(url, { headers: { Authorization: 'Bearer ' + token } })
    .then((res) => (res.ok ? res.json() : null))
    .catch((err) => {
      console.log(err);
      return null;
    });
}

/**
 * Persist sensor time offset(s) for a trial.
 * @param {string|number} trialID
 * @param {number|{ left: number, right?: number, offsetsByDevice?: object }} offsetSec
 *   Legacy single offset, { left, right }, or { offsetsByDevice } with one entry per EvoWalk.
 */
export async function saveSensorOffset(trialID, offsetSec) {
  const token = localStorage.getItem("token");
  const url = window.SERVER_URL + `/a/set-sensor-offset`;
  let body;
  if (typeof offsetSec === 'number') {
    body = {
      trial_id: trialID,
      offset_sec: offsetSec,
      offset_sec_left: offsetSec,
    };
  } else if (offsetSec && offsetSec.offsetsByDevice) {
    body = {
      trial_id: trialID,
      offsets_by_device: offsetSec.offsetsByDevice,
    };
  } else {
    body = {
      trial_id: trialID,
      offset_sec: offsetSec.left,
      offset_sec_left: offsetSec.left,
      offset_sec_right: offsetSec.right ?? 0,
    };
  }
  return fetch(url, {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then((res) => (res.ok ? res.json() : Promise.reject(res)))
    .catch((err) => { console.log(err); throw err; });
}

/**
 * Store clinician foot-contact marks in the same contact columns the pipeline
 * writes (heel_left_contact, toe_right_off, …).
 * POST /a/save-contact-annotations
 */
export async function saveContactAnnotations(trialID, annotations) {
  if (trialID == null || trialID === '') {
    throw new Error('trial id required');
  }
  const payload = contactAnnotationsPayload(trialID, annotations);
  if (!payload.events.length) {
    throw new Error('no annotations');
  }
  const token = localStorage.getItem('token');
  const url = window.SERVER_URL + '/a/save-contact-annotations';
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw res;
  return res.json();
}

export async function getMetricsSummary(trialIDList, metricNameList) {
    const trialParams = trialIDList.map(trial_id => `trial_id=${trial_id}`);
    const metricNameParams = metricNameList.map(name => `name=${name}`);
    const url = "/api/get-metrics-summary?"
                .concat(trialParams.join("&"))
                .concat("&")
                .concat(metricNameParams.join("&"));
    const token = localStorage.getItem("token");
    return fetch(window.SERVER_URL + url,{
      headers:{
        Authorization:'Bearer '+token
      }
    })
            .then(res => res.json())
            .catch(err => console.log(err))
}
